// KI-70: a myta-streams slot is launched with `-ForceRes 1920x1080` and every density/quality baseline
// assumes that pixel budget — the browser viewport must never push UE above it.
export const STREAM_PIXEL_BUDGET = 1920 * 1080;

export interface StreamResolution {
  width: number;
  height: number;
}

// The descriptor the PS frontend itself sends for MatchViewportRes (WebRtcPlayerController's
// onMatchViewportResolutionCallback → the `Command` to-streamer message, id 51, one JSON string).
export interface ResolutionCommand {
  'Resolution.Width': number;
  'Resolution.Height': number;
}

export type StreamResolutionCapPlan =
  | { action: 'skip'; reason: 'not-laid-out' }
  | { action: 'unchanged'; target: StreamResolution }
  | { action: 'send'; target: StreamResolution };

export interface StreamResolutionCap {
  plan: (width: number, height: number) => StreamResolutionCapPlan;
  markSent: (target: StreamResolution) => void;
  reset: () => void;
}

// Encoders (NVENC, 4:2:0) want even dimensions; the element's odd pixel is absorbed by the browser upscale.
export const roundToEven = (value: number): number => 2 * Math.round(value / 2);

// Both axes are scaled by the same factor, so the element's aspect ratio is kept: the browser's upscale
// fills it with no bars and no stretching, and the lib's input coordinate translation stays consistent.
// A fixed 1920x1080 on a non-16:9 element would letterbox or distort. Null = the element is not laid out
// (detached, 0x0), which is not a resolution to send.
export const capStreamResolution = (
  width: number,
  height: number,
  pixelBudget: number = STREAM_PIXEL_BUDGET,
): StreamResolution | null => {
  if (!(width > 0) || !(height > 0)) {
    return null;
  }

  const scale = Math.min(1, Math.sqrt(pixelBudget / (width * height)));

  return {
    width: roundToEven(width * scale),
    height: roundToEven(height * scale),
  };
};

export const isSameResolution = (
  a: StreamResolution | null,
  b: StreamResolution | null,
): boolean => !!a && !!b && a.width === b.width && a.height === b.height;

export const toResolutionCommand = ({
  width,
  height,
}: StreamResolution): ResolutionCommand => ({
  'Resolution.Width': width,
  'Resolution.Height': height,
});

// Remembers what UE was last told, so a resize that maps to the same capped target (or the wrapper's
// post-mount resize re-fire) does not cost UE another r.SetRes. Only a delivered command is recorded:
// the caller marks it after the lib accepted it, so a refused send is retried by the next trigger.
export const createStreamResolutionCap = (
  pixelBudget: number = STREAM_PIXEL_BUDGET,
): StreamResolutionCap => {
  let lastSent: StreamResolution | null = null;

  return {
    plan: (width, height) => {
      const target = capStreamResolution(width, height, pixelBudget);

      if (!target) {
        return { action: 'skip', reason: 'not-laid-out' };
      }

      if (isSameResolution(target, lastSent)) {
        return { action: 'unchanged', target };
      }

      return { action: 'send', target };
    },
    markSent: (target) => {
      lastSent = target;
    },
    reset: () => {
      lastSent = null;
    },
  };
};
